<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Visitor Reports</h5>
        <p class="page-sub">Compliance, security and occupancy analytics for your visitor programme</p>
      </div>
      <div style="display:flex;gap:8px;align-items:center;">
        <input v-model="from" type="date" class="filter-input" @change="loadAll" />
        <span style="font-size:13px;color:#7a90b0;">to</span>
        <input v-model="to" type="date" class="filter-input" @change="loadAll" />
      </div>
    </div>

    <!-- Tab bar -->
    <div class="tab-bar">
      <button class="tab-btn" :class="{ 'tab-btn--active': tab === 'compliance' }" @click="tab = 'compliance'">
        <font-awesome-icon icon="clipboard-check" /> Compliance
      </button>
      <button class="tab-btn" :class="{ 'tab-btn--active': tab === 'security' }" @click="tab = 'security'">
        <font-awesome-icon icon="shield-alt" /> Security
      </button>
      <button class="tab-btn" :class="{ 'tab-btn--active': tab === 'occupancy' }" @click="tab = 'occupancy'">
        <font-awesome-icon icon="chart-bar" /> Occupancy
      </button>
    </div>

    <!-- COMPLIANCE TAB -->
    <div v-if="tab === 'compliance'">
      <div v-if="loadingC" class="sk-kpis">
        <div v-for="n in 6" :key="n" class="sk-kpi"></div>
      </div>
      <div v-else-if="compliance" class="kpi-grid">
        <div class="kpi-card"><div class="kpi-num">{{ compliance.total_visits }}</div><div class="kpi-label">Total Visits</div></div>
        <div class="kpi-card"><div class="kpi-num">{{ compliance.nda_signed_count }}</div><div class="kpi-label">NDA Signed</div></div>
        <div class="kpi-card kpi-card--amber">
          <div class="kpi-num">{{ compliance.nda_pending_count }}</div>
          <div class="kpi-label">NDA Not Signed</div>
        </div>
        <div class="kpi-card"><div class="kpi-num">{{ compliance.avg_duration_minutes ? `${Math.round(compliance.avg_duration_minutes)}m` : '—' }}</div><div class="kpi-label">Avg Duration</div></div>
        <div class="kpi-card"><div class="kpi-num">{{ compliance.pre_registered_vs_walkin?.pre_registered ?? 0 }}</div><div class="kpi-label">Pre-Registered</div></div>
        <div class="kpi-card"><div class="kpi-num">{{ compliance.pre_registered_vs_walkin?.walked_in ?? 0 }}</div><div class="kpi-label">Walk-Ins</div></div>
      </div>

      <div v-if="compliance" class="two-col-grid" style="margin-top:20px;">
        <div class="card">
          <div class="card-title">Status Breakdown</div>
          <div class="status-list">
            <div v-for="(count, status) in compliance.by_status" :key="status" class="status-row">
              <span class="status-dot" :class="`status-dot--${status}`"></span>
              <span class="status-name">{{ statusLabel(status) }}</span>
              <span class="status-count">{{ count }}</span>
              <div class="status-bar-wrap">
                <div class="status-bar" :class="`status-bar--${status}`" :style="{ width: compliance.total_visits ? `${(count / compliance.total_visits * 100)}%` : '0%' }"></div>
              </div>
            </div>
          </div>
        </div>
        <div class="card">
          <div class="card-title">By Visitor Type</div>
          <div v-if="compliance.top_visitor_types?.length" class="status-list">
            <div v-for="t in compliance.top_visitor_types" :key="t.type ?? 'none'" class="status-row">
              <span class="status-name">{{ t.type ?? 'Untyped' }}</span>
              <span class="status-count">{{ t.count }}</span>
            </div>
          </div>
          <div v-else class="card-empty">No visitor type data for this period.</div>
        </div>
      </div>
    </div>

    <!-- SECURITY TAB -->
    <div v-if="tab === 'security'">
      <div v-if="loadingS" class="sk-kpis"><div v-for="n in 5" :key="n" class="sk-kpi"></div></div>
      <div v-else-if="security" class="kpi-grid">
        <div class="kpi-card kpi-card--red"><div class="kpi-num">{{ security.watchlist_matches_total }}</div><div class="kpi-label">Watchlist Matches (All Time)</div></div>
        <div class="kpi-card kpi-card--red"><div class="kpi-num">{{ security.watchlist_matches_this_month }}</div><div class="kpi-label">Matches This Month</div></div>
        <div class="kpi-card kpi-card--amber"><div class="kpi-num">{{ security.blocked_attempts }}</div><div class="kpi-label">Blocked Attempts</div></div>
        <div class="kpi-card kpi-card--amber"><div class="kpi-num">{{ security.denied_visits }}</div><div class="kpi-label">Denied Visits</div></div>
        <div class="kpi-card kpi-card--amber"><div class="kpi-num">{{ security.unresolved_flags }}</div><div class="kpi-label">Flagged Profiles</div></div>
      </div>
      <div v-if="security" class="card" style="margin-top:20px;">
        <div class="card-title">Security Summary</div>
        <div v-if="security.watchlist_matches_total === 0 && security.blocked_attempts === 0" class="card-empty">
          <font-awesome-icon icon="check-circle" style="color:#16a34a;font-size:22px;margin-bottom:8px;" /><br>
          No security incidents in the selected period.
        </div>
        <p v-else style="font-size:13px;color:#4a5e78;line-height:1.7;">
          {{ security.watchlist_matches_total }} total watchlist hits recorded.
          {{ security.blocked_attempts }} visitors were blocked from checking in.
          {{ security.unresolved_flags }} visitor profiles are currently flagged for review.
        </p>
      </div>
    </div>

    <!-- OCCUPANCY TAB -->
    <div v-if="tab === 'occupancy'">
      <div v-if="loadingO" class="sk-kpis"><div v-for="n in 4" :key="n" class="sk-kpi"></div></div>
      <div v-else-if="occupancy" class="kpi-grid">
        <div class="kpi-card"><div class="kpi-num">{{ occupancy.current_occupancy }}</div><div class="kpi-label">Currently On Site</div></div>
        <div class="kpi-card"><div class="kpi-num">{{ occupancy.average_daily }}</div><div class="kpi-label">Avg Daily Visitors</div></div>
        <div class="kpi-card kpi-card--blue"><div class="kpi-num">{{ occupancy.peak_day ?? '—' }}</div><div class="kpi-label">Busiest Day</div></div>
        <div class="kpi-card kpi-card--blue"><div class="kpi-num">{{ occupancy.peak_hour != null ? `${occupancy.peak_hour}:00` : '—' }}</div><div class="kpi-label">Peak Hour</div></div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'

export default {
  name: 'VisitorReports',
  setup() { return { toast: useToast() } },
  data() {
    const now = new Date()
    const from = new Date(now); from.setDate(from.getDate() - 30)
    return {
      tab: 'compliance',
      from: from.toISOString().slice(0, 10),
      to: now.toISOString().slice(0, 10),
      compliance: null, loadingC: false,
      security:   null, loadingS: false,
      occupancy:  null, loadingO: false,
    }
  },
  created() { this.loadAll() },
  methods: {
    loadAll() { this.loadCompliance(); this.loadSecurity(); this.loadOccupancy() },
    async loadCompliance() {
      this.loadingC = true
      try {
        const r = await axios.get('visitors/reports/compliance', { params: { from: this.from, to: this.to } })
        this.compliance = r.data.data ?? r.data
      } catch { useToast().error('Failed to load compliance report.') }
      finally { this.loadingC = false }
    },
    async loadSecurity() {
      this.loadingS = true
      try {
        const r = await axios.get('visitors/reports/security', { params: { from: this.from, to: this.to } })
        this.security = r.data.data ?? r.data
      } catch { useToast().error('Failed to load security report.') }
      finally { this.loadingS = false }
    },
    async loadOccupancy() {
      this.loadingO = true
      try {
        const r = await axios.get('visitors/reports/occupancy', { params: { from: this.from, to: this.to } })
        this.occupancy = r.data.data ?? r.data
      } catch { useToast().error('Failed to load occupancy report.') }
      finally { this.loadingO = false }
    },
    statusLabel(s) {
      return { pre_registered: 'Pre-Registered', checked_in: 'Checked In', checked_out: 'Checked Out',
               no_show: 'No Show', cancelled: 'Cancelled', pending_approval: 'Pending Approval',
               approved: 'Approved', denied_at_entry: 'Denied', auto_closed: 'Auto-Closed' }[s] ?? s
    },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.filter-input { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; font-family:inherit; }
.filter-input:focus { border-color:#004AAD; }

.tab-bar { display:flex; gap:4px; background:#f4f7fb; border-radius:10px; padding:4px; margin-bottom:20px; }
.tab-btn { display:inline-flex; align-items:center; gap:6px; height:34px; padding:0 16px; border-radius:8px; border:none; background:transparent; font-size:13px; font-weight:600; color:#7a90b0; cursor:pointer; font-family:inherit; transition:.15s; }
.tab-btn:hover { background:#fff; color:#1a2b45; }
.tab-btn--active { background:#fff; color:#004AAD; box-shadow:0 1px 4px rgba(0,0,0,.08); }

.kpi-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(150px,1fr)); gap:12px; }
.kpi-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:16px; }
.kpi-card--red    { border-color:#fca5a5; background:#fff5f5; }
.kpi-card--amber  { border-color:#fde68a; background:#fffbeb; }
.kpi-card--blue   { border-color:#bfdbfe; background:#eff6ff; }
.kpi-num  { font-size:26px; font-weight:800; color:#1a2b45; margin-bottom:4px; }
.kpi-card--red   .kpi-num { color:#dc2626; }
.kpi-card--amber .kpi-num { color:#d97706; }
.kpi-card--blue  .kpi-num { color:#1d4ed8; }
.kpi-label { font-size:12px; color:#7a90b0; font-weight:500; }

.two-col-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(300px,1fr)); gap:16px; }
.card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:18px; }
.card-title { font-size:13px; font-weight:700; color:#1a2b45; margin-bottom:14px; }
.card-empty { text-align:center; padding:24px 0; font-size:13px; color:#7a90b0; }

.status-list { display:flex; flex-direction:column; gap:8px; }
.status-row  { display:flex; align-items:center; gap:8px; font-size:13px; }
.status-dot  { width:8px; height:8px; border-radius:50%; flex-shrink:0; }
.status-dot--checked_in      { background:#22c55e; }
.status-dot--checked_out     { background:#60a5fa; }
.status-dot--pre_registered  { background:#f59e0b; }
.status-dot--no_show         { background:#f87171; }
.status-dot--cancelled       { background:#cbd5e1; }
.status-dot--pending_approval { background:#a78bfa; }
.status-dot--denied_at_entry { background:#ef4444; }
.status-name  { flex:1; color:#4a5e78; }
.status-count { font-weight:700; color:#1a2b45; min-width:30px; text-align:right; }
.status-bar-wrap { width:80px; height:6px; background:#f0f4f8; border-radius:3px; overflow:hidden; }
.status-bar { height:100%; border-radius:3px; background:#004AAD; transition:width .3s; }
.status-bar--checked_out     { background:#60a5fa; }
.status-bar--checked_in      { background:#22c55e; }
.status-bar--pre_registered  { background:#f59e0b; }
.status-bar--no_show         { background:#f87171; }
.status-bar--cancelled       { background:#cbd5e1; }

.sk-kpis { display:grid; grid-template-columns:repeat(auto-fill,minmax(150px,1fr)); gap:12px; }
.sk-kpi  { height:80px; border-radius:12px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
</style>
